import type { SxProps, Theme } from '@mui/material';
import type { Position } from 'common/types/dashboard/dashboardDefinition';

export const DEFAULT_GRID_COLUMNS = 3;

// Row height for a 1x1 tile. `minmax` rather than a fixed height so a content card with a long body
// grows instead of clipping, while empty data tiles still keep a consistent footprint.
const GRID_ROW_MIN_HEIGHT_PX = 168;

export const dashboardGridSx = (columns: number): SxProps<Theme> => ({
  display: 'grid',
  gap: 3,
  gridAutoRows: `minmax(${GRID_ROW_MIN_HEIGHT_PX}px, auto)`,
  // Below sm the schema's column/span placement is dropped entirely (see dashboardTileSx) and every
  // tile becomes a full-width row, because a 3-across grid is unreadable on a phone.
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: `repeat(${columns}, minmax(0, 1fr))`,
  },
  width: '100%',
});

// Schema positions are 0-indexed; CSS grid lines start at 1.
export const dashboardTileSx = (position: Position | undefined): SxProps<Theme> => {
  const { x = 0, y = 0, w = 1, h = 1 } = position ?? {};
  return {
    gridColumn: { xs: 'auto', sm: `${x + 1} / span ${w}` },
    gridRow: { xs: 'auto', sm: `${y + 1} / span ${h}` },
    minWidth: 0,
  };
};

export const tileCardSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  border: theme => `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: theme => `${theme.shape.radius.m}px`,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  height: '100%',
  padding: 3,
};

export const tileHeaderSx: SxProps<Theme> = {
  alignItems: 'flex-start',
  display: 'flex',
  gap: 1.5,
  justifyContent: 'space-between',
};

export const tileTitleSx: SxProps<Theme> = {
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: '22px',
};

export const tileSubtitleSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[500],
  fontSize: '0.8125rem',
  lineHeight: '18px',
};

// Keeps the type chip and the edit button together as one top-right cluster, so the header's
// space-between still puts the title on the left and both trailing pieces on the right.
export const tileHeaderActionsSx: SxProps<Theme> = {
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: 0.5,
};

export const tileTypeChipSx: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: theme => theme.palette.neutral[100],
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  color: theme => theme.palette.neutral[500],
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 600,
  height: 20,
  letterSpacing: '0.02em',
  padding: '0 6px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

export const tileBadgeSx: SxProps<Theme> = {
  alignItems: 'center',
  alignSelf: 'flex-start',
  backgroundColor: theme => theme.palette.brand[100],
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  color: theme => theme.palette.brand[800],
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 600,
  height: 22,
  padding: '0 8px',
};

export const tileHighlightValueSx: SxProps<Theme> = {
  fontSize: '1.75rem',
  fontWeight: 700,
  lineHeight: '34px',
};

export const tileHighlightCaptionSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[500],
  fontSize: '0.8125rem',
};

export const tileBodySx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[600],
  fontSize: '0.875rem',
  lineHeight: '20px',
};

export const tileFieldRowSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.75,
};

export const tileFieldChipSx: SxProps<Theme> = {
  backgroundColor: theme => theme.palette.neutral[100],
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  color: theme => theme.palette.neutral[600],
  display: 'inline-flex',
  fontSize: 12,
  lineHeight: '20px',
  padding: '0 8px',
};

// Stands in for the chart/table/value that a data endpoint will eventually fill. A plain block
// rather than the Skeleton primitive, which animates and would read as "still loading".
export const tilePlaceholderSx: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: theme => theme.palette.neutral[50],
  border: theme => `1px dashed ${theme.palette.neutral[200]}`,
  borderRadius: theme => `${theme.shape.radius.s}px`,
  color: theme => theme.palette.neutral[400],
  display: 'flex',
  flex: 1,
  fontSize: 12,
  justifyContent: 'center',
  marginTop: 'auto',
  minHeight: 48,
  padding: 1,
  textAlign: 'center',
};

export const tileTagRowSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.75,
};
